/**
 * Profile (tabbed, role-aware dashboard).
 *
 * Shared top-strip (welcome banner + role badge) plus a tab bar whose
 * items vary by role:
 *
 *   PARTICIPANT -> Overview · My listings · Orders
 *   DEALER      -> Overview · My listings · Orders · Subscription
 *   ADMIN       -> Revenue  · Orders & disputes · Settings · Users
 *
 * Each tab is a stand-alone component in ./tabs.
 */
import * as React from "react";
import { ExternalLink, PackageX, ShoppingBag, Store, TrendingUp } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import type { Product } from "../../lib/types";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../components/ui/Card";
import { Tabs, TabItem } from "../../components/ui/Tabs";
import { AdminSettingsTab } from "./tabs/AdminSettingsTab";
import { AdminDashboardTab } from "./tabs/AdminDashboardTab";
import { AdminDisputesTab } from "./tabs/AdminDisputesTab";
import { AdminUsersTab } from "./tabs/AdminUsersTab";
import { AdminCategoriesTab } from "./tabs/AdminCategoriesTab";
import { AdminProductsTab } from "./tabs/AdminProductsTab";
import { MyListingsTab } from "./tabs/MyListingsTab";
import { EditProfileTab } from "./tabs/EditProfileTab";
import { WatchlistTab } from "./tabs/WatchlistTab";
import { ReviewModal } from "../../components/reviews/ReviewModal";
import { StarRating } from "../../components/reviews/StarRating";
import { ShipModal } from "../../components/orders/ShipModal";
import { Modal } from "../../components/ui/Modal";
import type { SellerReview } from "../../lib/types";
import { Truck, Package } from "lucide-react";

// Order type used only for dashboard typing; backend returns full Order shape.
interface Order {
  id: string;
  buyer_id: string;
  seller_id: string;
  product_id: string;
  product_title: string;
  total_amount: number;
  platform_fee: number;
  seller_payout: number;
  status: string;
  payment_status: string;
  created_at: string;
  // Phase 18 — shipment tracking fields (optional on legacy docs).
  carrier?: string | null;
  tracking_number?: string | null;
  tracking_url?: string | null;
  shipped_at?: string | null;
  // Phase 21 — delivery confirmation timestamp.
  delivered_at?: string | null;
}

type DashboardPayload = {
  role: "PARTICIPANT" | "DEALER" | "ADMIN";
  user: any;
  orders: Order[];
  listings: Product[];
  inventory_stats?: {
    active_listings: number;
    sold_listings: number;
    inactive_listings: number;
    lifetime_orders: number;
    lifetime_revenue: number;
  };
  subscription?: {
    tier: string;
    status: string;
    expires_at?: string | null;
    stripe_customer_id?: string | null;
  };
  platform_stats?: any;
};

export default function Profile() {
  const { user } = useAuth();
  const [data, setData] = React.useState<DashboardPayload | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [tab, setTab] = React.useState<string>("overview");

  React.useEffect(() => {
    (async () => {
      try {
        const r = await api.get<DashboardPayload>("/users/me/dashboard");
        setData(r.data);
        // Default landing tab: admin sees the Phase 25 "what needs my
        // attention" dashboard, everyone else sees their overview.
        setTab(r.data.role === "ADMIN" ? "dashboard" : "overview");
      } catch (err) {
        setError(extractApiError(err));
      }
    })();
  }, []);

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16">
        <Card elevated>
          <CardBody className="text-center">
            <h1 className="text-2xl font-black text-brand mb-2">Error</h1>
            <p className="text-ink-muted">{error}</p>
          </CardBody>
        </Card>
      </div>
    );
  }
  if (!data || !user) {
    return (
      <div className="py-16">
        <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto" />
      </div>
    );
  }

  const tabs: TabItem[] =
    data.role === "ADMIN"
      ? [
          { value: "dashboard", label: "Dashboard" },
          { value: "revenue", label: "Revenue" },
          { value: "disputes", label: "Orders & disputes" },
          { value: "users", label: "Users" },
          { value: "products", label: "Products" },
          { value: "categories", label: "Categories" },
          { value: "settings", label: "Settings" },
          { value: "edit", label: "Edit profile" },
        ]
      : data.role === "DEALER"
        ? [
            { value: "overview", label: "Overview" },
            { value: "listings", label: "My listings" },
            { value: "orders", label: "Orders" },
            { value: "watchlist", label: "Watchlist" },
            { value: "subscription", label: "Subscription" },
            { value: "edit", label: "Edit profile" },
          ]
        : [
            { value: "overview", label: "Overview" },
            { value: "listings", label: "My listings" },
            { value: "orders", label: "Orders" },
            { value: "watchlist", label: "Watchlist" },
            { value: "edit", label: "Edit profile" },
          ];

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-8 py-8 md:py-12">
      <header className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-ink-muted text-sm uppercase tracking-wider">
            {data.role.toLowerCase()} dashboard
          </p>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-1">
            Welcome, @{user.username}
          </h1>
        </div>
        <Badge
          variant={data.role === "ADMIN" ? "solid" : data.role === "DEALER" ? "brand" : "outline"}
        >
          {data.role}
        </Badge>
      </header>

      <Tabs items={tabs} value={tab} onChange={setTab} testid="profile-tabs" className="mb-6" />

      {/* Participant / Dealer views ------------------------------------- */}
      {data.role !== "ADMIN" && tab === "overview" && (
        <OverviewView data={data} />
      )}
      {data.role !== "ADMIN" && tab === "listings" && <MyListingsTab />}
      {data.role !== "ADMIN" && tab === "orders" && (
        <OrdersCard orders={data.orders} />
      )}
      {data.role !== "ADMIN" && tab === "watchlist" && <WatchlistTab />}
      {data.role === "DEALER" && tab === "subscription" && (
        <DealerSubscriptionCard data={data} />
      )}
      {data.role !== "ADMIN" && tab === "edit" && <EditProfileTab />}

      {/* Admin views ---------------------------------------------------- */}
      {data.role === "ADMIN" && tab === "dashboard" && <AdminDashboardTab />}
      {data.role === "ADMIN" && tab === "revenue" && <AdminRevenueView />}
      {data.role === "ADMIN" && tab === "disputes" && <AdminDisputesTab />}
      {data.role === "ADMIN" && tab === "users" && <AdminUsersTab />}
      {data.role === "ADMIN" && tab === "products" && <AdminProductsTab />}
      {data.role === "ADMIN" && tab === "categories" && <AdminCategoriesTab />}
      {data.role === "ADMIN" && tab === "settings" && <AdminSettingsTab />}
      {data.role === "ADMIN" && tab === "edit" && <EditProfileTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview (participant + dealer) -- stats + CTA
// ---------------------------------------------------------------------------
function OverviewView({ data }: { data: DashboardPayload }) {
  return (
    <div className="flex flex-col gap-6">
      {data.role === "PARTICIPANT" && <ParticipantUpgradeCard />}
      {data.role === "DEALER" && <DealerStatsGrid data={data} />}
      <OrdersCard orders={data.orders} />
      {/* Phase 18 — seller-side sales card with Mark-as-Shipped CTA. */}
      <SalesCard />
    </div>
  );
}

function ParticipantUpgradeCard() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const upgrade = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.post("/stripe/create-subscription-checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      setError(extractApiError(err));
      setLoading(false);
    }
  };

  return (
    <Card
      elevated
      data-testid="participant-upgrade-card"
      className="border-2 border-ink"
    >
      <CardBody className="flex items-center gap-5 flex-wrap">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold mb-1">Become a Dealer — lower fees</h2>
          <p className="text-ink-muted">
            Dealers pay a lower platform commission (5% vs 10%) plus weekly visibility boosts.
          </p>
          {error && (
            <p className="text-brand text-sm font-medium mt-2">{error}</p>
          )}
        </div>
        <Button
          variant="primary"
          size="lg"
          loading={loading}
          onClick={upgrade}
          data-testid="upgrade-to-dealer-btn"
        >
          Upgrade to Dealer
        </Button>
      </CardBody>
    </Card>
  );
}

function DealerStatsGrid({ data }: { data: DashboardPayload }) {
  const stats = data.inventory_stats;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard
        label="Active listings"
        value={stats?.active_listings ?? 0}
        icon={<Store className="h-5 w-5" />}
        testid="stat-active"
      />
      <StatCard
        label="Sold"
        value={stats?.sold_listings ?? 0}
        icon={<ShoppingBag className="h-5 w-5" />}
        testid="stat-sold"
      />
      <StatCard
        label="Inactive"
        value={stats?.inactive_listings ?? 0}
        icon={<PackageX className="h-5 w-5" />}
        testid="stat-inactive"
      />
      <StatCard
        label="Lifetime revenue"
        value={`$${(stats?.lifetime_revenue ?? 0).toFixed(2)}`}
        icon={<TrendingUp className="h-5 w-5" />}
        testid="stat-revenue"
      />
    </div>
  );
}

function DealerSubscriptionCard({ data }: { data: DashboardPayload }) {
  const sub = data.subscription;
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const openPortal = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.post("/stripe/create-portal-session", {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      setError(extractApiError(err));
      setLoading(false);
    }
  };

  const isAdminGranted = sub?.status === "ADMIN_GRANTED";

  return (
    <Card elevated data-testid="dealer-subscription-card">
      <CardHeader>
        <div>
          <h2 className="text-xl font-bold">Subscription</h2>
          <p className="text-ink-muted text-sm mt-1">
            Tier: <strong>{sub?.tier}</strong> · Status: <strong>{sub?.status}</strong>
          </p>
        </div>
        {isAdminGranted ? (
          <Badge variant="solid">Admin-granted</Badge>
        ) : (
          <Badge variant="brand">Active</Badge>
        )}
      </CardHeader>
      <CardBody>
        {isAdminGranted ? (
          <p className="text-ink-muted">
            Your dealer access was granted directly by an administrator. No
            billing is tied to this account.
          </p>
        ) : (
          <>
            {error && <p className="text-brand text-sm font-medium mb-2">{error}</p>}
            <Button
              variant="outline"
              size="md"
              onClick={openPortal}
              loading={loading}
              leadingIcon={<ExternalLink className="h-5 w-5" />}
              data-testid="manage-subscription-btn"
            >
              Manage subscription (Stripe)
            </Button>
          </>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Admin revenue view -- retained from Phase 6, now a tab.
// ---------------------------------------------------------------------------
function AdminRevenueView() {
  const [lifetime, setLifetime] = React.useState<any>(null);
  const [weekly, setWeekly] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const [l, w] = await Promise.all([
          api.get("/admin/revenue"),
          api.get("/admin/revenue/weekly", { params: { weeks: 8 } }),
        ]);
        setLifetime(l.data);
        setWeekly(w.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="py-12">
        <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto" />
      </div>
    );
  }
  if (!lifetime) return null;

  const maxTotal = Math.max(1, ...(weekly?.series ?? []).map((w: any) => w.total_revenue));

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Lifetime revenue"
          value={`$${lifetime.total_revenue.toFixed(2)}`}
          icon={<TrendingUp className="h-5 w-5" />}
          testid="admin-stat-revenue"
        />
        <StatCard
          label="Order commissions"
          value={`$${lifetime.orders.commissions.toFixed(2)}`}
          testid="admin-stat-commissions"
        />
        <StatCard
          label="GMV"
          value={`$${lifetime.orders.gmv.toFixed(2)}`}
          testid="admin-stat-gmv"
        />
        <StatCard
          label="Subscription MRR"
          value={`$${lifetime.subscriptions.total.toFixed(2)}`}
          testid="admin-stat-mrr"
        />
      </div>

      <Card elevated data-testid="admin-weekly-chart">
        <CardHeader>
          <div>
            <h2 className="text-xl font-bold">Revenue — last 8 weeks</h2>
            <p className="text-ink-muted text-sm mt-1">
              Commissions + subscriptions + boosts per ISO week (Monday start).
            </p>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-8 items-end gap-2 h-40">
            {(weekly?.series ?? []).map((w: any) => (
              <div key={w.week_start} className="flex flex-col items-center justify-end h-full">
                <div
                  className="w-full bg-ink rounded-t-md relative"
                  style={{
                    height: `${Math.max(4, (w.total_revenue / maxTotal) * 100)}%`,
                  }}
                  aria-label={`${w.week_start}: $${w.total_revenue.toFixed(2)}`}
                >
                  {w.total_revenue > 0 && (
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-ink whitespace-nowrap">
                      ${w.total_revenue.toFixed(0)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-8 gap-2 mt-2">
            {(weekly?.series ?? []).map((w: any) => (
              <div
                key={w.week_start + "-label"}
                className="text-[10px] text-ink-muted text-center font-mono"
              >
                {w.week_start.slice(5)}
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      <div className="grid md:grid-cols-3 gap-3">
        <StatCard label="Orders" value={lifetime.orders.orders} testid="admin-orders" />
        <StatCard label="Subscriptions" value={lifetime.subscriptions.count} testid="admin-subs" />
        <StatCard label="Boosts" value={lifetime.boosts.count} testid="admin-boosts" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------
function StatCard({
  label,
  value,
  icon,
  testid,
}: {
  label: string;
  value: number | string;
  icon?: React.ReactNode;
  testid?: string;
}) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-surface-border bg-white p-4"
    >
      <div className="flex items-center justify-between mb-1 text-ink-muted">
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
        {icon}
      </div>
      <div className="text-2xl font-black font-display text-ink">{value}</div>
    </div>
  );
}

function OrdersCard({ orders: initialOrders }: { orders: Order[] }) {
  const { user } = useAuth();
  // Phase 21: local mirror of the orders prop so optimistic updates
  // (status flip after Confirm Delivery) survive without a parent
  // refetch. The prop seeds it; subsequent confirmations patch the
  // affected row in place.
  const [orders, setOrders] = React.useState<Order[]>(initialOrders);
  React.useEffect(() => setOrders(initialOrders), [initialOrders]);

  // Map of order_id -> the buyer's existing review (or undefined if none).
  // We only probe DELIVERED orders where the current user is the buyer.
  const [myReviews, setMyReviews] = React.useState<Record<string, SellerReview | null>>({});
  const [modalOrder, setModalOrder] = React.useState<Order | null>(null);

  // Phase 21 — Confirm Delivery state. `confirmOrder` opens the modal;
  // `confirmBusy` debounces the network call; `confirmError` surfaces
  // any backend error inline so the user knows what happened.
  const [confirmOrder, setConfirmOrder] = React.useState<Order | null>(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState<string | null>(null);

  const runConfirmDelivery = async () => {
    if (!confirmOrder) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      const r = await api.post<Order>(
        `/orders/${confirmOrder.id}/confirm-delivery`,
      );
      // Optimistically patch the row to DELIVERED so the Leave-a-review
      // CTA appears without a full refresh.
      setOrders((prev) =>
        prev.map((o) => (o.id === confirmOrder.id ? { ...o, ...r.data } : o)),
      );
      setConfirmOrder(null);
    } catch (e) {
      setConfirmError(extractApiError(e));
    } finally {
      setConfirmBusy(false);
    }
  };

  // Probe on every render where the orders list changes. Phase 17:
  // GET /api/orders/{id}/review returns 404 when no review exists, and
  // we treat that as "show the Leave-a-review button". Done per-order
  // because the dashboard endpoint doesn't carry this field today; a
  // future optimisation could batch it there.
  React.useEffect(() => {
    if (!user) return;
    const targets = orders.filter(
      (o) => o.status === "DELIVERED" && o.buyer_id === user.id,
    );
    targets.forEach(async (o) => {
      if (o.id in myReviews) return;
      try {
        const r = await api.get<SellerReview>(`/orders/${o.id}/review`);
        setMyReviews((prev) => ({ ...prev, [o.id]: r.data }));
      } catch (e) {
        // 404 = no review yet; cache as null so we don't re-fetch.
        setMyReviews((prev) => ({ ...prev, [o.id]: null }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, user?.id]);

  const handleReviewSaved = (orderId: string, review: SellerReview) => {
    setMyReviews((prev) => ({ ...prev, [orderId]: review }));
  };

  return (
    <Card elevated data-testid="orders-card">
      <CardHeader>
        <h2 className="text-xl font-bold">Recent orders</h2>
      </CardHeader>
      <CardBody>
        {orders.length === 0 ? (
          <p className="text-ink-muted">No orders yet.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-surface-border">
            {orders.slice(0, 10).map((o) => {
              const isBuyer = !!user && o.buyer_id === user.id;
              const canReview = isBuyer && o.status === "DELIVERED";
              const existing = canReview ? myReviews[o.id] : undefined;
              return (
                <li
                  key={o.id}
                  className="py-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-ink truncate">
                      {o.product_title}
                    </div>
                    <div className="text-xs text-ink-muted">
                      {new Date(o.created_at).toLocaleDateString()} · {o.status}
                    </div>
                    {existing && (
                      <div className="mt-1">
                        <StarRating value={existing.rating} size={14} />
                      </div>
                    )}
                    {/* Phase 18 — buyer-side tracking display. */}
                    {o.status === "SHIPPED" && (
                      <div
                        className="mt-2 text-xs inline-flex items-center gap-1.5 text-emerald-700"
                        data-testid={`order-tracking-${o.id}`}
                      >
                        <Truck className="h-3.5 w-3.5" aria-hidden />
                        <span>
                          Shipped
                          {o.shipped_at
                            ? ` on ${new Date(o.shipped_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                            : ""}
                          {o.carrier ? ` · ${o.carrier}` : ""}
                          {o.tracking_number ? ` #${o.tracking_number}` : ""}
                        </span>
                        {o.tracking_url && (
                          <a
                            href={o.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline font-semibold ml-1"
                          >
                            Track package →
                          </a>
                        )}
                      </div>
                    )}
                    {o.status === "PAID" && (
                      <div className="mt-2 text-xs text-ink-muted inline-flex items-center gap-1.5">
                        <Package className="h-3.5 w-3.5" aria-hidden />
                        Awaiting shipment
                      </div>
                    )}
                    {/* Phase 21 — Confirm Delivery CTA. Buyer-only on
                        SHIPPED rows. Closes AUDIT §17-delivered-gap /
                        §18-no-auto-delivered: without this, the Leave-a-
                        review CTA above (gated on DELIVERED) was
                        unreachable in production. */}
                    {isBuyer && o.status === "SHIPPED" && (
                      <div className="mt-2">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => {
                            setConfirmError(null);
                            setConfirmOrder(o);
                          }}
                          data-testid={`confirm-delivery-btn-${o.id}`}
                        >
                          Confirm delivery
                        </Button>
                        <p className="text-xs text-ink-muted mt-1">
                          Did your part arrive? Confirm to leave a review.
                        </p>
                      </div>
                    )}
                    {o.status === "DELIVERED" && o.delivered_at && (
                      <div
                        className="mt-2 text-xs inline-flex items-center gap-1.5 text-emerald-700"
                        data-testid={`order-delivered-${o.id}`}
                      >
                        <Truck className="h-3.5 w-3.5" aria-hidden />
                        Delivered on{" "}
                        {new Date(o.delivered_at).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {canReview && (
                      <Button
                        size="sm"
                        variant={existing ? "outline" : "primary"}
                        onClick={() => setModalOrder(o)}
                        data-testid={`order-review-btn-${o.id}`}
                      >
                        {existing ? "Edit review" : "Leave a review"}
                      </Button>
                    )}
                    <span className="font-display font-black text-ink">
                      ${o.total_amount.toFixed(2)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>

      {modalOrder && (
        <ReviewModal
          open={!!modalOrder}
          onClose={() => setModalOrder(null)}
          orderId={modalOrder.id}
          existing={myReviews[modalOrder.id] ?? null}
          onSaved={(rev) => handleReviewSaved(modalOrder.id, rev)}
        />
      )}

      {/* Phase 21 — Confirm Delivery modal. Minimal native confirmation;
          no extra dependency. */}
      {confirmOrder && (
        <Modal
          open={!!confirmOrder}
          onClose={confirmBusy ? () => {} : () => setConfirmOrder(null)}
          title="Confirm delivery"
          description="This marks the order delivered and lets you leave a review."
          testid="confirm-delivery-modal"
        >
          <p className="text-sm text-ink mb-1">
            <strong>{confirmOrder.product_title}</strong>
          </p>
          <p className="text-sm text-ink-muted mb-4">
            Confirm you received this part? This action can&apos;t be undone.
          </p>
          {confirmError && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 mb-3">
              {confirmError}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOrder(null)}
              disabled={confirmBusy}
              data-testid="confirm-delivery-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={runConfirmDelivery}
              disabled={confirmBusy}
              data-testid="confirm-delivery-submit"
            >
              {confirmBusy ? "Confirming…" : "Yes, I received it"}
            </Button>
          </div>
        </Modal>
      )}
    </Card>
  );
}

/**
 * <SalesCard /> — Phase 18.
 * Seller-side view of orders. Fetches `/api/orders/sales`, renders
 * each row, and surfaces "Mark as Shipped" for PAID orders. Hidden
 * when the user has no sales (avoids dashboard clutter for buyers
 * who never list anything).
 */
function SalesCard() {
  const [sales, setSales] = React.useState<Order[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [shipOrder, setShipOrder] = React.useState<Order | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<Order[]>("/orders/sales?limit=20");
      setSales(r.data);
    } catch {
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading) return null;
  if (sales.length === 0) return null;

  return (
    <Card elevated data-testid="sales-card">
      <CardHeader>
        <h2 className="text-xl font-bold">Recent sales</h2>
      </CardHeader>
      <CardBody>
        <ul className="flex flex-col divide-y divide-surface-border">
          {sales.map((o) => (
            <li
              key={o.id}
              className="py-3 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="font-semibold text-ink truncate">
                  {o.product_title}
                </div>
                <div className="text-xs text-ink-muted">
                  {new Date(o.created_at).toLocaleDateString()} · {o.status}
                </div>
                {o.status === "SHIPPED" && (
                  <div className="text-xs text-emerald-700 inline-flex items-center gap-1.5 mt-1">
                    <Truck className="h-3.5 w-3.5" aria-hidden />
                    {o.carrier ?? "Carrier"}
                    {o.tracking_number ? ` · ${o.tracking_number}` : ""}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {o.status === "PAID" && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => setShipOrder(o)}
                    data-testid={`ship-btn-${o.id}`}
                  >
                    Mark as Shipped
                  </Button>
                )}
                <span className="font-display font-black text-ink">
                  ${o.total_amount.toFixed(2)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </CardBody>

      {shipOrder && (
        <ShipModal
          open={!!shipOrder}
          onClose={() => setShipOrder(null)}
          orderId={shipOrder.id}
          onShipped={() => {
            setShipOrder(null);
            load();
          }}
        />
      )}
    </Card>
  );
}
